import type { ListingDetail, SearchResult } from '@/lib/types';

/**
 * The browsing history behind the home page's "Recently viewed" and "More in …"
 * rails. It lives in localStorage, not the backend: no endpoint records views,
 * and a rail that renders from a stored snapshot costs the home page nothing.
 *
 * The snapshot is the `SearchResult` shape so `ResultCard` can draw it as-is.
 * It is what the listing looked like when it was viewed; a price that changed
 * since is corrected the moment the card is tapped.
 */

export interface RecentView {
  result: SearchResult;
  /** Of the listing, so "browsing related" knows what to fetch more of. */
  category: { slug: string; name: string };
  viewedAt: number;
}

/** v2: entries carry cover and category glyph. Bumping the key retires v1 shapes. */
const KEY = 'sag.recent.v2';
/** Two rails' worth. Beyond that it is surveillance, not convenience. */
const CAP = 12;

/** `localStorage` is absent in unit tests and throws in some private-browsing modes. */
function store(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function readRecentViews(): RecentView[] {
  try {
    const raw = store()?.getItem(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // A malformed entry (older shape, hand-edited storage) is dropped, not thrown on.
    return parsed.filter(
      (v): v is RecentView =>
        typeof v === 'object' &&
        v !== null &&
        typeof (v as RecentView).result?.id === 'string' &&
        typeof (v as RecentView).category?.slug === 'string',
    );
  } catch {
    return [];
  }
}

export function clearRecentViews(): void {
  try {
    store()?.removeItem(KEY);
  } catch {
    // Nothing to clear, or nowhere to clear it from.
  }
}

export function recordView(listing: ListingDetail): void {
  const entry: RecentView = {
    result: {
      id: listing.id,
      title: listing.title,
      slug: listing.slug,
      description: listing.description,
      priceType: listing.priceType,
      priceMin: listing.priceMin,
      priceMax: listing.priceMax,
      currency: listing.currency,
      durationMinutes: listing.durationMinutes,
      // Snapshot time, not publish time. Nothing renders it; the field just
      // keeps the shape a complete SearchResult.
      createdAt: new Date().toISOString(),
      providerId: listing.provider.id,
      businessName: listing.provider.businessName,
      providerSlug: listing.provider.slug,
      city: listing.provider.city,
      ratingAvg: listing.provider.ratingAvg,
      ratingCount: listing.provider.ratingCount,
      coverUrl: listing.images[0]?.url ?? null,
      categoryName: listing.category.name,
      categoryIconName: listing.category.iconName ?? listing.category.parent?.iconName ?? null,
      distanceKm: null,
      score: 0,
      // A viewed-listing snapshot does not track favourite state; the card that
      // renders it (a rail, not the results list) never shows a heart anyway.
      isFavorited: false,
    },
    category: { slug: listing.category.slug, name: listing.category.name },
    viewedAt: Date.now(),
  };

  try {
    const rest = readRecentViews().filter((v) => v.result.id !== listing.id);
    store()?.setItem(KEY, JSON.stringify([entry, ...rest].slice(0, CAP)));
  } catch {
    // Full or forbidden storage loses a nicety, never the page.
  }
}

/* ── Recent searches: the terms the user actually typed ──────────────────── */

const SEARCH_KEY = 'sag.searches.v1';
/** One screenful of rows. */
const SEARCH_CAP = 8;

export function readRecentSearches(): string[] {
  try {
    const raw = store()?.getItem(SEARCH_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((t): t is string => typeof t === 'string') : [];
  } catch {
    return [];
  }
}

/** Only submitted searches land here — never keystrokes or filter changes. */
export function recordSearch(term: string): void {
  const clean = term.trim();
  if (!clean) return;
  try {
    const rest = readRecentSearches().filter((t) => t.toLowerCase() !== clean.toLowerCase());
    store()?.setItem(SEARCH_KEY, JSON.stringify([clean, ...rest].slice(0, SEARCH_CAP)));
  } catch {
    // Full or forbidden storage loses a nicety, never the search itself.
  }
}

export function clearRecentSearches(): void {
  try {
    store()?.removeItem(SEARCH_KEY);
  } catch {
    // Nothing to clear, or nowhere to clear it from.
  }
}
